import type { TryOnFineTuningSettingsType, TryOnProvider, TryOnType } from '@zakeke/zakeke-3d-core';
import { showNotification } from '../notificationToaster';
import React from 'react';
import OverlayLoading from 'src/components/widgets/overlayLoading';
import VtoFinetuning from 'src/components/widgets/vtoFinetuning/vtoFinetuning';
import { T } from 'src/shared/helpers';

export interface TryOnFineTuningProps {
    provider: TryOnProvider;
    mesh?: Blob;
    meshUrl?: string;
    settings?: TryOnFineTuningSettingsType;
    modelLoaded?: boolean;
    type?: TryOnType;
    onSettingsCallback?: (settings: TryOnFineTuningSettingsType) => void;
    onCloseCallback?: () => void;
}

const TryOnFineTuning: React.FC<TryOnFineTuningProps> = ({
    provider,
    mesh,
    meshUrl,
    settings,
    modelLoaded = false,
    type,
    onSettingsCallback,
    onCloseCallback
}) => {
    const [modelBlob, setModelBlob] = React.useState<Blob | null>(mesh || null);
    const [finetuningSettings, setFinetuningSettings] = React.useState<TryOnFineTuningSettingsType | null>(settings || null);
    const [finetuningFrameReady, setFinetuningFrameReady] = React.useState(false);
    const [isFineTuningFullScreen, setIsFineTuningFullScreen] = React.useState(false);

    console.log('🎨 TryOnFineTuning - provider:', provider, 'meshUrl:', meshUrl, 'mesh size:', mesh?.size);

    // Sincronizza i props esterni con lo stato interno
    React.useEffect(() => {
        if (mesh && mesh !== modelBlob) {
            setModelBlob(mesh);
        }
    }, [mesh]);

    React.useEffect(() => {
        if (settings && settings !== finetuningSettings) {
            setFinetuningSettings(settings);
        }
    }, [settings]);

    // Se modelLoaded è true ma non abbiamo il blob, usa quello che è stato caricato
    React.useEffect(() => {
        if (modelLoaded && !modelBlob && mesh) {
            setModelBlob(mesh);
        }
    }, [modelLoaded, mesh, modelBlob]);

    const handleSettingsChange = (newSettings: TryOnFineTuningSettingsType) => {
        setFinetuningSettings(newSettings);
        if (onSettingsCallback) {
            onSettingsCallback(newSettings);
        }
    };

    const handleSettingsSave = () => {
        if (finetuningSettings && onSettingsCallback) {
            onSettingsCallback(finetuningSettings);
            showNotification({
                message: T._('Finetuning settings saved', 'Admin'),
                type: 'success',
                dismiss: { duration: 3000 }
            });
        }
    };

    const handleClose = () => {
        console.log('🔴 TryOnFineTuning: handleClose called');
        if (isFineTuningFullScreen) {
            // Se è in fullscreen, prima esci dal fullscreen
            setIsFineTuningFullScreen(false);
        } else {
            // Se non è in fullscreen, chiudi completamente
            if (onCloseCallback) {
                console.log('🔴 TryOnFineTuning: calling onCloseCallback');
                onCloseCallback();
            }
        }
    };

    // Gestore separato per chiudere completamente (sempre)
    const handleForceClose = () => {
        console.log('🔴 TryOnFineTuning: handleForceClose called');
        if (onCloseCallback) {
            onCloseCallback();
        }
    };

    // Se non abbiamo un provider valido, non renderizzare nulla
    if (!provider) {
        console.log('🚫 TryOnFineTuning: No provider, not rendering');
        return null;
    }

    console.log('🎯 TryOnFineTuning rendering with:', {
        provider,
        modelBlob: !!modelBlob,
        mesh: !!mesh,
        modelLoaded,
        settings,
        finetuningFrameReady
    });

    return (
        <>
            {(!modelBlob || !finetuningFrameReady) && <OverlayLoading forceShow />}
            {modelBlob && (
                <VtoFinetuning
                    model={modelBlob}
                    modelUrl={meshUrl}
                    onSettingsChange={handleSettingsChange}
                    onSettingsSave={handleSettingsSave}
                    currentSettings={finetuningSettings}
                    onLoaded={() => setFinetuningFrameReady(true)}
                    provider={provider}
                    onFullScreen={setIsFineTuningFullScreen}
                />
            )}
            {/* Pulsante per chiudere il fine tuning quando è in fullscreen */}
            {isFineTuningFullScreen && onCloseCallback && (
                <div
                    style={{
                        position: 'fixed',
                        top: '20px',
                        right: '20px',
                        zIndex: 10001,
                        background: 'white',
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.3)'
                    }}
                    onClick={handleForceClose}
                    title='Close Fine Tuning'
                >
                    ✕
                </div>
            )}
            {/* Pulsante per chiudere quando NON è in fullscreen (opzionale) */}
            {!isFineTuningFullScreen && onCloseCallback && (
                <div
                    style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        zIndex: 1001,
                        background: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        borderRadius: '50%',
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        fontSize: '16px'
                    }}
                    onClick={handleForceClose}
                    title='Close Fine Tuning'
                >
                    ✕
                </div>
            )}
        </>
    );
};

export default TryOnFineTuning;
